import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Select } from 'antd';

import PropTypes from 'prop-types';
import { isNullOrUndefined } from '../../helpers/helpers';

import { getIdMap } from 'ontraccr-common/lib/Common';
import ProfileAvatar from '../ProfileAvatar';
import SelectLabelFilter from '../../forms/FormBuilder/FormFields/SelectLabelFilter';
import OntraccrTag from '../OntraccrTag';
import ConflictWarningIcon from '../../schedule/FieldSchedule/ConflictPopover';
import {
  conflictsByIdPropType,
  getConflictTextColor,
  useConflictTagRender,
} from '../../schedule/FieldSchedule/ConflictSelectRenderers';

export default function FilteredUserSelector({
  users = [],
  onChange,
  value,
  mode = 'multiple',
  allowClear,
  placeholder,
  filterStyle = null,
  selectStyle = {},
  className = '',
  disabled = false,
  showLabels = false,
  conflictsByUserId = {},
}) {
  const userToLabel = useSelector((state) => state.users.userToLabel);
  const labels = useSelector((state) => state.labels);
  const [filterLabels, setFilterLabels] = useState([]);

  const labelMap = useMemo(() => getIdMap(labels), [labels]);

  const filteredUsers = useMemo(() => (
    filterLabels.length === 0
      ? users
      : users.filter((user) => {
        const {
          [user.id]: userLabels = [],
        } = userToLabel;
        const userLabelSet = new Set(userLabels);
        return filterLabels.every((label) => userLabelSet.has(label));
      })
  ), [users, userToLabel, filterLabels]);

  const tagRender = useConflictTagRender(conflictsByUserId);

  return (
    <div className={`filter-user-select ${className}`}>
      <SelectLabelFilter
        selected={filterLabels}
        onSelectedChanged={setFilterLabels}
        style={filterStyle}
        type="users"
        disabled={disabled}
      />
      <Select
        className="OnTraccrSelect"
        optionLabelProp="label"
        optionFilterProp="label"
        mode={mode}
        onChange={onChange}
        value={value}
        showSearch
        allowClear={allowClear}
        placeholder={placeholder}
        style={selectStyle}
        disabled={disabled}
        tagRender={tagRender}
      >
        {filteredUsers.map((user) => {
          const conflicts = conflictsByUserId[user.id];

          return (
            <Select.Option key={user.id} value={user.id} label={user.name}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!isNullOrUndefined(user.distanceKm) && (
                  <OntraccrTag
                    color="blue"
                    style={{ fontSize: 11, textAlign: 'center', marginRight: 0 }}
                  >
                    {user?.distanceKm?.toFixed(1)} km
                  </OntraccrTag>
                )}
                <ProfileAvatar
                  role={user.position}
                  name={user.name}
                  textColor={getConflictTextColor(conflicts) ?? null}
                  labels={showLabels
                    ? userToLabel[user.id]?.map(id => labelMap[id]?.title)
                    : []}
                />
                <ConflictWarningIcon conflicts={conflicts} />
              </div>
            </Select.Option>
          );
        })}
      </Select>
    </div>
  );
}

FilteredUserSelector.propTypes = {
  disabled: PropTypes.bool,
  showLabels: PropTypes.bool,
  conflictsByUserId: conflictsByIdPropType,
};

FilteredUserSelector.defaultProps = {
  disabled: false,
  showLabels: false,
  conflictsByUserId: {},
};
